/**
 * Hero image uploader for the event Basics tab.
 *
 * Enhancement 6: two slots (list card 16:9, detail hero 21:9). Each slot goes
 * through a crop modal (react-easy-crop) so the admin gets a consistent
 * aspect ratio without leaving the app. Uploads to the existing GridFS
 * /api/media/upload with owner_type=event_hero and is_public=true.
 *
 * Backward-compat: still writes hero_image_url / hero_image_media_id so
 * legacy readers keep working. The list/detail-specific fields are stored
 * alongside as hero_image_list_* and hero_image_detail_*.
 */
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Trash2, Image as ImageIcon, Crop as CropIcon } from 'lucide-react';
import api from '@/utils/api';
import HeroCropModal from './HeroCropModal';

const SLOTS = [
  {
    key: 'detail',
    label: 'Detail page hero (21:9)',
    hint: 'Shown at the top of the public event page. Wide landscape.',
    aspect: 21 / 9,
    urlKey: 'hero_image_detail_url',
    mediaKey: 'hero_image_detail_media_id',
    isPrimary: true,
  },
  {
    key: 'list',
    label: 'Event card (16:9)',
    hint: 'Shown on the event listing grid. Slightly squarer.',
    aspect: 16 / 9,
    urlKey: 'hero_image_list_url',
    mediaKey: 'hero_image_list_media_id',
    isPrimary: false,
  },
];

const HeroImageUploader = ({
  heroImageUrl,               // legacy field
  heroMediaId,                 // legacy field
  heroImageDetailUrl = '',
  heroImageDetailMediaId = null,
  heroImageListUrl = '',
  heroImageListMediaId = null,
  onChange,
}) => {
  // Track a source image + which slot the crop dialog is targeting.
  const [pending, setPending] = useState(null); // { slotKey, sourceUrl }
  const [busySlot, setBusySlot] = useState(null);
  const [err, setErr] = useState('');
  const detailInputRef = useRef(null);
  const listInputRef = useRef(null);

  const currentUrlFor = (slot) => {
    if (slot.key === 'detail') return heroImageDetailUrl || heroImageUrl || '';
    if (slot.key === 'list') return heroImageListUrl || '';
    return '';
  };

  const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const handleFilePick = async (slot, file) => {
    if (!file) return;
    setErr('');
    try {
      const src = await readAsDataUrl(file);
      setPending({ slotKey: slot.key, sourceUrl: src, aspect: slot.aspect });
    } catch (e) {
      setErr('Could not read that file. Try a JPG or PNG.');
    }
  };

  const uploadBlob = async (slot, blob) => {
    setBusySlot(slot.key);
    setErr('');
    try {
      // Bundle 75 Task 5 fix - do NOT set an explicit Content-Type
      // header when posting FormData. Axios + the browser XHR layer
      // will emit the correct `multipart/form-data; boundary=...`
      // string automatically. Manually setting `multipart/form-data`
      // without a boundary strips the boundary and produces an
      // unparseable body on the origin, which surfaces to the user
      // as a Cloudflare 520 "origin sent a response CF could not
      // parse" because the endpoint never gets a chance to reply.
      // Bundle 97 - cut event-editor hero upload over to the
      // hardened v2 endpoint. v2 stores bytes-as-is via GridFS
      // with no Pillow on the request path, so the CF-520
      // latency mode that plagued v1 cannot recur.
      const fd = new FormData();
      fd.append('file', new File([blob], `hero_${slot.key}.jpg`, { type: 'image/jpeg' }));
      fd.append('owner_type', 'event_hero');
      fd.append('is_public', 'true');
      const { data } = await api.post('/media/upload/v2', fd);
      const url = data.url || data.public_url;
      const patch = {
        [slot.urlKey]: url,
        [slot.mediaKey]: data.id,
      };
      // Keep legacy fields in sync from the detail slot so existing readers
      // (and the "hero image" publish-check) keep working.
      if (slot.isPrimary) {
        patch.hero_image_url = url;
        patch.hero_image_media_id = data.id;
      }
      onChange(patch);
    } catch (e) {
      // Bundle 75 Task 5 - surface a friendly, specific error string.
      // Backend now returns a structured JSON detail on every failure
      // path (413 too-large, 415 wrong-type, 500 compression) so the
      // uploader can render exactly what went wrong.
      const detail = e?.response?.data?.detail
        || e?.message
        || 'Upload failed. Please retry with a JPG or PNG under 20 MB.';
      setErr(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setBusySlot(null);
    }
  };

  const clearSlot = (slot) => {
    const patch = {
      [slot.urlKey]: '',
      [slot.mediaKey]: null,
    };
    if (slot.isPrimary) {
      patch.hero_image_url = '';
      patch.hero_image_media_id = null;
    }
    onChange(patch);
  };

  return (
    <div className="space-y-4" data-testid="hero-image-uploader">
      <div className="grid gap-4 md:grid-cols-2">
        {SLOTS.map((slot) => {
          const url = currentUrlFor(slot);
          const inputRef = slot.key === 'detail' ? detailInputRef : listInputRef;
          const busy = busySlot === slot.key;
          return (
            <div key={slot.key} className="space-y-1.5" data-testid={`hero-slot-${slot.key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-secondary-700 font-medium">
                    {slot.label}
                  </div>
                  <div className="text-[11px] text-neutral-500">{slot.hint}</div>
                </div>
                {url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-accent-700 h-7 px-2"
                    onClick={() => clearSlot(slot)}
                    data-testid={`hero-remove-${slot.key}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {url ? (
                <div
                  className="relative rounded-xl overflow-hidden border border-neutral-200 bg-neutral-100 group"
                  style={{ aspectRatio: slot.aspect }}
                >
                  <img src={url} alt={slot.label} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end justify-end p-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-white/95"
                      onClick={() => inputRef.current?.click()}
                      disabled={busy}
                      data-testid={`hero-replace-${slot.key}`}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" /> Replace
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={busy}
                  className="w-full rounded-xl border-2 border-dashed border-neutral-300 hover:border-secondary-400 hover:bg-secondary-50/30 transition-colors flex flex-col items-center justify-center text-neutral-500 py-6"
                  style={{ aspectRatio: slot.aspect }}
                  data-testid={`hero-upload-${slot.key}`}
                >
                  <ImageIcon className="h-7 w-7 mb-1.5 opacity-70" />
                  <span className="text-sm font-medium">{busy ? 'Uploading…' : 'Click to upload'}</span>
                  <span className="text-[11px] mt-0.5 flex items-center gap-1"><CropIcon className="h-3 w-3" /> crop after selecting</span>
                </button>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  handleFilePick(slot, f);
                }}
                className="hidden"
              />
            </div>
          );
        })}
      </div>
      {err && <div className="text-xs text-accent-700">{err}</div>}

      {pending && (
        <HeroCropModal
          open={!!pending}
          onClose={() => setPending(null)}
          imageSrc={pending.sourceUrl}
          aspect={pending.aspect}
          title={`Crop ${pending.slotKey === 'detail' ? 'detail hero (21:9)' : 'event card (16:9)'}`}
          onCropped={(blob) => {
            const slot = SLOTS.find((s) => s.key === pending.slotKey);
            if (slot) uploadBlob(slot, blob);
          }}
        />
      )}
    </div>
  );
};

export default HeroImageUploader;
